/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import "./ConditionalPlugin.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $findMatchingParent, $insertNodeToNearestRoot, mergeRegister } from "@lexical/utils";
import { $dfs, $filter, addClassNamesToElement } from "@lexical/utils";
import { isContractValidationPage } from "helpers/utils";
import type { ElementNode, LexicalNode, NodeKey, ParagraphNode } from "lexical";
import {
    $createParagraphNode,
    $createTextNode,
    $getPreviousSelection,
    $getRoot,
    $getSelection,
    $isElementNode,
    $isParagraphNode,
    $isRangeSelection,
    $isRootNode,
    $nodesOfType,
    $setSelection,
    COMMAND_PRIORITY_LOW,
    createCommand,
    DELETE_CHARACTER_COMMAND,
    INSERT_PARAGRAPH_COMMAND,
    KEY_ARROW_DOWN_COMMAND,
    KEY_ARROW_LEFT_COMMAND,
    KEY_ARROW_RIGHT_COMMAND,
    KEY_ARROW_UP_COMMAND,
    KEY_DELETE_COMMAND,
    KEY_DOWN_COMMAND,
    RootNode,
    TextNode,
} from "lexical";
import { useEffect, useState } from "react";

import { $isConditionalNode } from "../ConditionalNode";
import {
    $createConditionalContainerNode,
    $isConditionalContainerNode,
    ConditionalContainerNode,
} from "./ConditionalContainerNode";
import {
    $createConditionalContentNode,
    $isConditionalContentNode,
    ConditionalContentNode,
} from "./ConditionalContentNode";
import { $createConditionalTitleNode, $isConditionalTitleNode, ConditionalTitleNode } from "./ConditionalTitleNode";

export const INSERT_CONDITIONAL_COMMAND = createCommand<ParagraphNode | LexicalNode[]>();
export const TOGGLE_CONDITIONAL_COMMAND = createCommand<NodeKey>();

export default function ConditionalPlugin(): null {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (!editor.hasNodes([ConditionalContainerNode, ConditionalTitleNode, ConditionalContentNode])) {
            throw new Error(
                "ConditionalPlugin: ConditionalContainerNode, ConditionalTitleNode, or ConditionalContentNode not registered on editor",
            );
        }

        const onEscapeUp = () => {
            const selection = $getSelection();
            if ($isRangeSelection(selection) && selection.isCollapsed() && selection.anchor.offset === 0) {
                const container = $findMatchingParent(selection.anchor.getNode(), $isConditionalContainerNode);

                if ($isConditionalContainerNode(container)) {
                    const parent = container.getParent<ElementNode>();
                    if (
                        parent !== null &&
                        parent.getFirstChild<LexicalNode>() === container &&
                        selection.anchor.key === container.getFirstDescendant<LexicalNode>()?.getKey()
                    ) {
                        container.insertBefore($createParagraphNode());
                    }
                }
            }

            return false;
        };

        const onEscapeDown = () => {
            const selection = $getSelection();
            if ($isRangeSelection(selection) && selection.isCollapsed()) {
                const container = $findMatchingParent(selection.anchor.getNode(), $isConditionalContainerNode);

                if ($isConditionalContainerNode(container)) {
                    const parent = container.getParent<ElementNode>();
                    if (parent !== null && parent.getLastChild<LexicalNode>() === container) {
                        const titleParagraph = container.getFirstDescendant<LexicalNode>();
                        const contentParagraph = container.getLastDescendant<LexicalNode>();

                        if (
                            (contentParagraph !== null &&
                                selection.anchor.key === contentParagraph.getKey() &&
                                selection.anchor.offset === contentParagraph.getTextContentSize()) ||
                            (titleParagraph !== null &&
                                selection.anchor.key === titleParagraph.getKey() &&
                                selection.anchor.offset === titleParagraph.getTextContentSize())
                        ) {
                            container.insertAfter($createParagraphNode());
                        }
                    }
                }
            }

            return false;
        };

        return mergeRegister(
            // Structure enforcing transformers for each node type. In case nesting structure is not
            // "Container > Title + Content" it'll unwrap nodes and convert it back
            // to regular content.
            editor.registerNodeTransform(ConditionalContainerNode, (node) => {
                if (!node.getShow() && isContractValidationPage()) {
                    node.remove();
                }
            }),

            editor.registerNodeTransform(ConditionalContentNode, (node) => {
                const parent = node.getParent<ElementNode>();
                if (!$isConditionalContainerNode(parent)) {
                    const children = node.getChildren<LexicalNode>();
                    for (const child of children) {
                        node.insertBefore(child);
                    }
                    node.remove();
                }
            }),

            editor.registerNodeTransform(ConditionalTitleNode, (node) => {
                const parent = node.getParent<ElementNode>();
                if (!$isConditionalContainerNode(parent)) {
                    node.replace($createParagraphNode().append(...node.getChildren<LexicalNode>()));
                    return;
                }
            }),

            editor.registerNodeTransform(ConditionalContainerNode, (node) => {
                const children = node.getChildren<LexicalNode>();
                if (
                    children.length !== 2 ||
                    !$isConditionalTitleNode(children[0]) ||
                    !$isConditionalContentNode(children[1])
                ) {
                    for (const child of children) {
                        node.insertBefore(child);
                    }
                    node.remove();
                }
            }),

            // This handles the case when container is collapsed and we delete its previous sibling
            // into it, it would cause collapsed content deleted (since it's display: none, and selection
            // swallows it when deletes single char). Instead we expand container, which is although
            // not perfect, but avoids bigger problem
            editor.registerCommand(
                DELETE_CHARACTER_COMMAND,
                () => {
                    const selection = $getSelection();

                    if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
                        return false;
                    }
                    const selectionNode = selection.anchor.getNode();
                    const conditionalContainerNode = $findMatchingParent(selectionNode, $isConditionalContainerNode);
                    const conditionalContentNode = $findMatchingParent(selectionNode, $isConditionalContentNode);
                    const newParagraphNode = $createParagraphNode().append($createTextNode(""));
                    const conditionalTextNodes = conditionalContainerNode?.getAllTextNodes();
                    /**
                     * If conditionalContainerNode :
                     * -> is a conditional node
                     * -> doesn't have more than 1 line of children
                     * -> doesn't have textNodes children
                     * so delete optional block
                     */
                    const hasEmptyContent =
                        conditionalContentNode &&
                        conditionalContentNode.getChildren().length < 2 &&
                        !conditionalTextNodes?.length;

                    if (conditionalContainerNode && hasEmptyContent) {
                        $setSelection(null);
                        conditionalContainerNode?.replace(newParagraphNode);
                    }

                    const topLevelElement = selectionNode.getTopLevelElement();
                    if (topLevelElement === null) {
                        return false;
                    }

                    const container = topLevelElement.getPreviousSibling<LexicalNode>();
                    if (!$isConditionalContainerNode(container) || container.getOpen()) {
                        return false;
                    }

                    container.setOpen(true);
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),

            // When collapsible conditional is the last child pressing down/right arrow will insert paragraph
            // below it to allow adding more content. It's similar what $insertBlockNode
            // (mainly for decorators), except it'll always be possible to continue adding
            // new content even if trailing paragraph is accidentally deleted
            editor.registerCommand(KEY_ARROW_DOWN_COMMAND, onEscapeDown, COMMAND_PRIORITY_LOW),

            editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, onEscapeDown, COMMAND_PRIORITY_LOW),

            // When collapsible conditional is the first child pressing up/left arrow will insert paragraph
            // above it to allow adding more content. It's similar what $insertBlockNode
            // (mainly for decorators), except it'll always be possible to continue adding
            // new content even if leading paragraph is accidentally deleted
            editor.registerCommand(KEY_ARROW_UP_COMMAND, onEscapeUp, COMMAND_PRIORITY_LOW),

            editor.registerCommand(KEY_ARROW_LEFT_COMMAND, onEscapeUp, COMMAND_PRIORITY_LOW),

            // Handling CMD+Enter to toggle collapsible conditional element collapsed state
            editor.registerCommand(
                INSERT_PARAGRAPH_COMMAND,
                () => {
                    const windowEvent = editor._window?.event as KeyboardEvent | undefined;
                    if (windowEvent && (windowEvent.ctrlKey || windowEvent.metaKey) && windowEvent.key === "Enter") {
                        const selection = $getPreviousSelection();
                        if ($isRangeSelection(selection) && selection.isCollapsed()) {
                            const parent = $findMatchingParent(
                                selection.anchor.getNode(),
                                (node) => $isElementNode(node) && !node.isInline(),
                            );

                            if ($isConditionalTitleNode(parent)) {
                                const container = parent.getParent<ElementNode>();
                                if ($isConditionalContainerNode(container)) {
                                    container.toggleOpen();
                                    $setSelection(selection.clone());
                                    return true;
                                }
                            }
                        }
                    }

                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                INSERT_CONDITIONAL_COMMAND,
                (node: ParagraphNode | LexicalNode[]) => {
                    editor.update(() => {
                        const title = $createConditionalTitleNode();
                        const conditionalContentNode = $createConditionalContentNode();
                        // check if it's inline selection -> so append the selected paragraph
                        if ($isParagraphNode(node as ParagraphNode)) {
                            conditionalContentNode.append(node as ParagraphNode);
                        } else {
                            (node as ParagraphNode[]).forEach((node) => {
                                conditionalContentNode.append(node);
                            });
                        }

                        $insertNodeToNearestRoot(
                            $createConditionalContainerNode(true, true).append(title, conditionalContentNode),
                        );
                        $setSelection(null);

                        const newConditionalContainer = $findMatchingParent(
                            conditionalContentNode,
                            $isConditionalContainerNode,
                        );
                        const nextSibling = newConditionalContainer?.getNextSibling();
                        // sometimes it adds an empty uncontroled paragraph
                        if ($isParagraphNode(nextSibling) && !nextSibling.getTextContent()) {
                            nextSibling.remove();
                        }
                        newConditionalContainer?.insertAfter($createParagraphNode());
                    });
                    return true;
                },
                COMMAND_PRIORITY_LOW,
            ),
        );
    }, [editor]);

    return null;
}
